// src/lib/schema/validators.js

/**
 * Validate that an object has all required fields
 * @param {object} obj - Object to validate
 * @param {string[]} requiredFields - Array of required field names
 * @returns {boolean} True if all required fields exist
 */
export function validateRequiredFields(obj, requiredFields) {
  if (!obj) return false;

  return requiredFields.every(field => {
    const value = obj[field];
    return value !== undefined && value !== null;
  });
}

/**
 * Validate that a URL is properly formatted
 * @param {string} url - URL to validate
 * @returns {boolean} True if URL is valid
 */
export function isValidUrl(url) {
  if (!url) return false;

  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Validate schema is properly formatted
 * @param {object} schema - Schema object to validate
 * @returns {boolean} True if schema is valid
 */
export function isValidSchema(schema) {
  if (!schema) return false;

  // Check for required fields in all schemas
  if (!schema['@context'] || !schema['@type']) {
    return false;
  }

  // Add type-specific validation based on schema['@type']
  switch (schema['@type']) {
    case 'Product':
      return validateRequiredFields(schema, ['name']);

    case 'Organization':
      return validateRequiredFields(schema, ['name']);

    case 'LocalBusiness':
    case 'CarpetStore':
      return validateRequiredFields(schema, ['name', 'address']);

    case 'FAQPage':
      return Boolean(schema.mainEntity && Array.isArray(schema.mainEntity) && schema.mainEntity.length > 0);

    case 'BreadcrumbList':
      return Boolean(schema.itemListElement && Array.isArray(schema.itemListElement) && schema.itemListElement.length > 0);

    default:
      // Basic validation for other schema types
      return true;
  }
}

/**
 * Validate that a price value is properly formatted
 * @param {number|string} price - Price to validate
 * @returns {boolean} True if price is valid
 */
export function isValidPrice(price) {
  if (price === undefined || price === null) return false;

  const numericPrice = Number(price);
  return !isNaN(numericPrice) && numericPrice >= 0;
}

/**
 * Validate that a currency code is properly formatted (3 uppercase letters)
 * @param {string} currency - Currency code to validate
 * @returns {boolean} True if currency code is valid
 */
export function isValidCurrency(currency) {
  if (!currency || typeof currency !== 'string') return false;

  return /^[A-Z]{3}$/.test(currency);
}

/**
 * Validate image URL or array of image URLs
 * @param {string|string[]} image - Image URL or array of URLs
 * @returns {boolean} True if all image URLs are valid
 */
export function isValidImage(image) {
  if (!image) return false;

  if (Array.isArray(image)) {
    if (image.length === 0) return false;
    return image.every(url => isValidUrl(url));
  }

  return isValidUrl(image);
}
